import React, { useState } from 'react';
import { Card, Divider, styled, useMediaQuery } from '@mui/material';
import { Avatar, Checkbox, Text } from 'library';
import { BusinessLocationPreferredWorker, LeanUser } from 'models';
import DefaultProfileImage from 'assets/icons/Profile.svg';
import { useUserContext } from 'UserContext';
import ListingName from 'parent-portal/providers/cards/SearchListing/ListingName';
import DocumentLinks from 'parent-portal/provider-profile/tabs/documents/DocumentShared';
import DownloadAllDocuments from 'parent-portal/provider-profile/tabs/documents/DownloadAllDocuments';
import SetPreferredModal from 'parent-portal/provider-reviews/HighStarProviderReview/SetPreferredModal';
import ExternalBusinessOutlawModal from 'parent-portal/providers/cards/SearchListing/ExternalBusinessOutlawModal';
import theme from 'theme';
import { IProviderSearch, IProviderBusinessOutlaw, IProviderPreferredWorker, WORKED } from '../models';
import { BannedButton, PreferredButton } from '.';

export function ProviderRow({
    provider,
    checked,
    handleCheck,
    workerType,
}: {
    provider: IProviderSearch;
    checked: boolean;
    handleCheck: (checked: boolean) => void;
    workerType: string;
}) {
    const userAvatar = provider.profile_image?.image;
    const small = useMediaQuery(theme.breakpoints.down('md'));

    const { user } = useUserContext();
    const businessId = user?.businesses[0].id;

    const [openBanModal, setOpenBanModal] = useState<boolean>(false);
    const [openPreferredModal, setOpenPreferredModal] = useState<boolean>(false);

    const [bannedLocations, setBannedLocations] = useState<IProviderBusinessOutlaw[]>(provider.business_outlaws ?? []);
    const [bannedAll, setBannedAll] = useState<boolean>(
        !!provider.business_outlaws?.length && !provider.business_outlaws?.[0]?.business_location,
    );

    const [preferredLocations, setPreferredLocations] = useState<IProviderPreferredWorker[]>(
        provider.preferred_workers ?? [],
    );
    const [preferredAll, setPreferredAll] = useState<boolean>(
        !!provider.preferred_workers?.length && !provider.preferred_workers?.[0]?.business_location,
    );

    function handleBanAction(bannedProvider: LeanUser, businessId?: number, businessLocationId?: number) {
        if (businessId) {
            setBannedAll(!!businessId && !bannedAll);
            setBannedLocations([]);
        } else if (businessLocationId) {
            if (
                bannedLocations.some(
                    (outlaw: IProviderBusinessOutlaw) => outlaw.business_location === businessLocationId,
                )
            ) {
                setBannedLocations(
                    bannedLocations.filter(
                        (outlaw: IProviderBusinessOutlaw) => outlaw.business_location !== businessLocationId,
                    ),
                );
            } else {
                setBannedLocations([
                    ...bannedLocations,
                    { id: -1, user: bannedProvider.id, business_location: businessLocationId },
                ]);
            }
        }
    }

    function handlePrefer(preferredWorkers: BusinessLocationPreferredWorker[]) {
        const allPreferred = preferredWorkers.every((preferredWorker) => preferredWorker.is_preferred_worker);
        if (allPreferred) {
            setPreferredAll(true);
            setPreferredLocations([]);
        } else {
            setPreferredAll(false);
            const preferredWorkerLocations: IProviderPreferredWorker[] = preferredWorkers
                .filter((preferredWorker) => preferredWorker.is_preferred_worker)
                .map((preferredWorker) => ({
                    id: preferredWorker.id,
                    user: provider.user.id,
                    business: preferredWorker.business.id,
                    business_location: preferredWorker.id,
                }));

            setPreferredLocations(preferredWorkerLocations);
        }
    }

    return (
        <>
            <Container>
                <CheckboxContainer>
                    <StyledCheckbox label="" checked={checked} onChange={handleCheck} />
                </CheckboxContainer>
                <ContentContainer small={small}>
                    <Container>
                        {userAvatar ? (
                            <Avatar src={userAvatar} style={{ height: 82, width: 82 }} />
                        ) : (
                            <StyledProfileImage />
                        )}
                        <StyledName>
                            <ListingName provider={provider} />
                        </StyledName>
                    </Container>
                    <DocumentAndButtonsContainer>
                        <DocumentsContainer>
                            <TitleContainer>
                                <Text>Documents</Text>
                                <DownloadAllDocuments provider={provider} />
                            </TitleContainer>
                            <DocumentLinks provider={provider} />
                        </DocumentsContainer>
                        <ButtonsContainer small={small}>
                            {workerType === WORKED && (
                                <BannedButton
                                    bannedAll={bannedAll}
                                    bannedLocations={bannedLocations}
                                    setOpenBanModal={setOpenBanModal}
                                />
                            )}
                            <PreferredButton
                                preferredAll={preferredAll}
                                preferredLocations={preferredLocations}
                                setOpenPreferredModal={setOpenPreferredModal}
                            />
                        </ButtonsContainer>
                    </DocumentAndButtonsContainer>
                </ContentContainer>
            </Container>
            <StyledDivider />
            {businessId && (
                <ExternalBusinessOutlawModal
                    user={provider.user}
                    businessId={businessId}
                    open={openBanModal}
                    onClose={() => setOpenBanModal(false)}
                    refreshProviderList={handleBanAction}
                />
            )}
            {businessId && (
                <SetPreferredModal
                    open={openPreferredModal}
                    provider={{
                        id: provider.user.id,
                        first_name: provider.user.first_name,
                        last_name: provider.user.last_name,
                        profile_picture: { image: userAvatar ?? '' },
                    }}
                    onSuccess={handlePrefer}
                    onClose={() => setOpenPreferredModal(false)}
                />
            )}
        </>
    );
}

const Container = styled('div')({
    display: 'flex',
});

const CheckboxContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
});

const StyledCheckbox = styled(Checkbox)({
    paddingRight: 15,
});

const StyledName = styled('div')({
    marginLeft: 15,
});

const StyledProfileImage = styled(DefaultProfileImage)({
    height: 82,
    width: 82,
    borderRadius: '50%',
    flexShrink: 0,
});

const ContentContainer = styled('div')(({ small }: { small: boolean }) => ({
    display: 'flex',
    flexDirection: small ? 'column' : 'row',
    justifyContent: 'space-between',
    width: '100%',
}));

const DocumentAndButtonsContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    minWidth: '50%',
});

const DocumentsContainer = styled(Card)({
    display: 'flex',
    flexDirection: 'column',
    padding: 10,
    marginTop: 10,
});

const TitleContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
});

const ButtonsContainer = styled('div')(({ small }: { small: boolean }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    flexDirection: small ? 'column' : 'row',
}));

const StyledDivider = styled(Divider)({
    margin: '20px 0',
});
